import { useEffect, useRef, useState } from "react";
import { X, SwitchCamera, Camera as CameraIcon, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";

export default function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("user");
  const [shot, setShot] = useState(null);

  const start = async (mode) => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
    } catch {
      toast.error("Camera not available — allow camera permission");
    }
  };

  useEffect(() => {
    start(facing);
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, [facing]);

  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 640; c.height = v.videoHeight || 640;
    c.getContext("2d").drawImage(v, 0, 0);
    setShot(c.toDataURL("image/jpeg", 0.8));
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#121212]/90 flex items-center justify-center p-4" data-testid="camera-modal">
      <div className="bg-[#18181B] border border-[#27272A] p-4 w-full max-w-md text-[#F4F4F5]">
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Live Photo</p>
          <button onClick={onClose} data-testid="camera-close"><X size={20} /></button>
        </div>
        <div className="aspect-square bg-black overflow-hidden">
          {shot ? <img src={shot} alt="captured" className="w-full h-full object-cover" /> : <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />}
        </div>
        <div className="flex items-center justify-center gap-4 mt-4">
          {!shot ? (
            <>
              <button onClick={() => setFacing(facing === "user" ? "environment" : "user")} className="flex items-center gap-2 border border-[#27272A] px-4 py-2 text-xs uppercase tracking-widest" data-testid="camera-switch">
                <SwitchCamera size={16} /> {facing === "user" ? "Back" : "Front"}
              </button>
              <button onClick={capture} className="bg-[#C5A059] text-[#121212] px-6 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="camera-capture">
                <CameraIcon size={16} /> Capture
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setShot(null)} className="flex items-center gap-2 border border-[#27272A] px-4 py-2 text-xs uppercase tracking-widest" data-testid="camera-retake">
                <RotateCcw size={16} /> Retake
              </button>
              <button onClick={() => onCapture(shot)} className="bg-[#C5A059] text-[#121212] px-6 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="camera-confirm">
                <Check size={16} /> Use Photo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
